import type { DeliveriesDeliveryFiltersResponse } from '@customerio/hydra-ui-api';
import type { IconName } from '@customerio/pluma-icons';
import { isEmptyString } from '@/utils/string';
import type { DeliveryFilterOption } from '../types';

/**
 * Campaign/newsletter/transactional-message → action-message option builders
 * for the Outbox filter bar. Ported from the Ember `filters` service item
 * mappers (`campaignItem`/`newsletterItem`/`transactionalItem`) and the
 * `<DeliveriesFilters>` `messages` / `filteredMessages` getters.
 *
 * SCOPE: newsletter segment-on-variation "All Variations" branch messages are
 * intentionally not built here (an advanced newsletter-variation feature) —
 * tracked as a follow-up. The campaign, newsletter, transactional-message, and
 * plain per-channel action messages are fully ported.
 */

/** Campaign type value the API reports for API-triggered broadcasts. */
const API_TRIGGERED_BROADCAST = 'triggered_broadcast';

const CAMPAIGN_ICON: IconName = 'campaigns';
const BROADCAST_ICON: IconName = 'broadcast';
const TRANSACTIONAL_ICON: IconName = 'transactional-api';

/** Ember `nonBlankName`: blank names render as "(Untitled)". */
function nonBlankName(name: string): string {
  return isEmptyString(name) ? '(Untitled)' : name;
}

/** Case-insensitive sort by label (Ember `sortByCaseInsensitive(['label'])`). */
function byLabel(options: DeliveryFilterOption[]): DeliveryFilterOption[] {
  return [...options].sort((a, b) => a.label.toLowerCase().localeCompare(b.label.toLowerCase()));
}

interface CampaignOptionsInput {
  /** Selected channel value (`''` = all). Newsletters/transactional are email-only. */
  channel: string;
  /** Drafts view hides newsletters (drafts can't target newsletters). */
  drafted: boolean;
}

/**
 * The campaign-select options. Campaigns always appear; transactional messages
 * and newsletters are appended only for the email channel (newsletters also
 * only when not in the drafts view), matching the Ember `messages` getter.
 *
 * Option `value` encoding (consumed by `buildDeliveriesListParams`):
 *  - campaign → the bare campaign id
 *  - newsletter → `newsletter_<id>`
 *  - transactional message → `transactional_<id>`
 */
export function buildCampaignOptions(
  filters: DeliveriesDeliveryFiltersResponse,
  { channel, drafted }: CampaignOptionsInput,
): DeliveryFilterOption[] {
  const showNewsletters = channel === 'email' && !drafted;
  const showTransactional = channel === 'email';

  const campaigns: DeliveryFilterOption[] = filters.campaigns.map((campaign) => ({
    value: String(campaign.id),
    label: nonBlankName(campaign.name),
    icon: campaign.type === API_TRIGGERED_BROADCAST ? BROADCAST_ICON : CAMPAIGN_ICON,
  }));

  const transactional: DeliveryFilterOption[] = showTransactional
    ? filters.transactional_messages.map((message) => ({
        value: `transactional_${message.id}`,
        label: nonBlankName(message.name),
        icon: TRANSACTIONAL_ICON,
      }))
    : [];

  const newsletters: DeliveryFilterOption[] = showNewsletters
    ? filters.newsletters.map((newsletter) => ({
        value: `newsletter_${newsletter.id}`,
        label: nonBlankName(newsletter.name),
        icon: BROADCAST_ICON,
      }))
    : [];

  return byLabel([...campaigns, ...transactional, ...newsletters]);
}

/**
 * Whether the action (message) filter should be shown for a campaign value.
 * Ember `showActionFilter`: hidden when a transactional message is selected.
 */
export function showActionFilter(campaignValue: string | null): boolean {
  return campaignValue == null || !campaignValue.startsWith('transactional_');
}

interface ActionOptionsInput {
  channel: string;
  drafted: boolean;
  /** The selected campaign value (encoded), or `null`. */
  campaignValue: string | null;
}

/**
 * The action-select options: the workspace's messages for the selected channel,
 * narrowed to the selected campaign/newsletter/transactional parent and (in the
 * drafts view) to draftable messages. Ported from `filteredMessages` +
 * `actionSelectOptions`. Option `value` is the message `action_id`.
 */
export function buildActionOptions(
  filters: DeliveriesDeliveryFiltersResponse,
  { channel, drafted, campaignValue }: ActionOptionsInput,
): DeliveryFilterOption[] {
  let messages = filters.messages.filter((message) => message.type === channel);

  if (drafted) {
    // Ember `draftable = isNone(newsletter_id)`.
    messages = messages.filter((message) => message.newsletter_id == null);
  }

  if (!isEmptyString(campaignValue)) {
    const id = campaignValue.replace(/^(newsletter|transactional)_/, '');
    const key: 'newsletter_id' | 'transactional_message_id' | 'campaign_id' = campaignValue.startsWith('newsletter_')
      ? 'newsletter_id'
      : campaignValue.startsWith('transactional_')
        ? 'transactional_message_id'
        : 'campaign_id';
    messages = messages.filter((message) => message[key] === id);
  }

  return messages.map((message) => ({ value: message.action_id, label: message.subject }));
}
